import { QueryClient, useMutation, useQueries, useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo } from "react";
import type { Job } from "@models/Job";
import type {
	JobSchedule,
	ScheduleApplication,
	ScheduleInvitation,
	ScheduleJobApplications,
	ScheduleMatchingConsultant,
	ScheduleShiftInput,
} from "@models/Schedule";
import { apiGet, apiPost, apiPut } from "../client";

export interface ScheduleRange {
	/** ISO date-times */
	startTime: string;
	endTime: string;
}

/**
 * Jobs scheduled within any of the `ranges`, one request per range so the
 * schedule can grow while scrolling without refetching what is loaded. A job
 * spanning several ranges is merged into one, with its shifts and applications
 * deduplicated. `loadedRanges` tells which ranges have data.
 */
export function useJobSchedule(
	ranges: ScheduleRange[],
	customerIds: string[],
	expand?: string[],
	include?: string[],
) {
	const results = useQueries({
		queries: ranges.map(({ startTime, endTime }) => ({
			queryKey: ["useJobSchedule", startTime, endTime, JSON.stringify(customerIds)],
			// No customers means "none selected" (or not loaded yet), not "all"
			enabled: customerIds.length > 0,
			queryFn: () =>
				apiGet<{ jobSchedules?: JobSchedule[] }>("/schedule/job", {
					startTime,
					endTime,
					customerIds: customerIds.join(","),
					expand: expand ? expand.join(",") : "",
					include: include ? include.join(",") : "",
				}),
			// Keeps the loaded weeks on screen while a changed unit filter refetches
			placeholderData: (previous: { jobSchedules?: JobSchedule[] } | undefined) => previous,
		})),
	});

	const jobSchedules = useMemo(() => {
		const byId = new Map<string, JobSchedule>();
		results.forEach(({ data }) =>
			(data?.jobSchedules || []).forEach((job) => {
				const existing = byId.get(job.id);
				byId.set(
					job.id,
					existing
						? {
								...existing,
								shifts: uniqueById([...existing.shifts, ...job.shifts]),
								applications: uniqueById([...(existing.applications || []), ...(job.applications || [])]),
							}
						: job,
				);
			}),
		);
		return [...byId.values()];
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [results.map((res) => `${res.dataUpdatedAt}:${res.isPlaceholderData}`).join()]);

	return {
		jobSchedules,
		loadedRanges: ranges.filter((_, i) => !customerIds.length || results[i]?.data !== undefined),
		isFetching: results.some((res) => res.isFetching),
	};
}

const uniqueById = <T extends { id: string }>(items: T[]) => [...new Map(items.map((item) => [item.id, item])).values()];

/**
 * Hook to get consultant schedule
 */
export function useConsultantSchedule(
	startTime: string,
	endTime: string,
	customerIds?: string[],
	expand?: string[],
	include?: string[],
	onError?: (errorCode: string) => void,
) {
	if (!customerIds) {
		return { consultantSchedules: [], isLoading: false };
	}

	const { data, ...res } = useQuery<any>({
		queryKey: [
			"useConsultantSchedule",
			startTime,
			endTime,
			JSON.stringify(customerIds),
			JSON.stringify(expand),
			JSON.stringify(include),
		],
		queryFn: () =>
			apiGet<any>("/schedule/consultant", {
				startTime,
				endTime,
				customerIds: customerIds ? customerIds.join(",") : "",
				expand: expand ? expand.join(",") : "",
				include: include ? include.join(",") : "",
			}),
		onError: (error: any) => {
			const errorCode = error?.response?.data?.code || error?.message || "UNKNOWN_ERROR";
			if (onError) {
				onError(errorCode);
			}
		},
	});

	return { ...data, ...res };
}

const SCHEDULE_QUERY_KEYS = {
	jobDetail: "schedule-job-detail",
	applications: "schedule-job-applications",
};

/**
 * The schedule endpoint leaves out shift breaks, the job description, who
 * created the job and the consultants' pictures, so the week schedule reads
 * them from the job itself (one cached request per job).
 */
export function useScheduleJobDetails(jobIds: string[]) {
	const results = useQueries({
		queries: jobIds.map((id) => ({
			queryKey: [SCHEDULE_QUERY_KEYS.jobDetail, id],
			queryFn: () => apiGet<Job>(`/job/${id}`, { expand: "shifts,metadata.createdBy,applications,applications.consultant" }),
			staleTime: 60_000,
		})),
	});

	const detailsByJobId: Record<string, Job> = {};
	results.forEach((res, i) => {
		if (res.data) detailsByJobId[jobIds[i]] = res.data;
	});
	return detailsByJobId;
}

/**
 * Applications (incl. pending ones, which the schedule endpoint leaves out) and
 * invitations per job.
 */
export function useScheduleJobApplications(jobIds: string[]) {
	const results = useQueries({
		queries: jobIds.map((id) => ({
			queryKey: [SCHEDULE_QUERY_KEYS.applications, id],
			queryFn: () =>
				apiGet<{ applications?: ScheduleApplication[]; invitations?: ScheduleInvitation[]; wouldBookShifts?: { id: string }[] }>(`/job/${id}/application`, {
					expand: "consultant,consultant.hasCustomerExperience,metadata",
					include: "invitations",
				}),
			staleTime: 60_000,
		})),
	});

	const byJobId: Record<string, ScheduleJobApplications> = {};
	results.forEach((res, i) => {
		if (res.data)
			byJobId[jobIds[i]] = {
				applications: res.data.applications || [],
				invitations: res.data.invitations || [],
				wouldBookShiftIds: (res.data.wouldBookShifts || []).map((s) => s.id),
			};
	});
	return byJobId;
}

/**
 * Consultants matching a job, listed in the "Bjud in konsulter" drawer when
 * requesting ("önska") consultants. The endpoint pages, so a large page is
 * asked for to get everyone (same as the dashboard does).
 */
export function fetchJobMatchingConsultants(queryClient: QueryClient, jobId: string) {
	return queryClient
		.fetchQuery({
			queryKey: ["schedule-matching-consultants", jobId],
			queryFn: () =>
				apiGet<{ totalCount: number; users: ScheduleMatchingConsultant[] }>(`/job/${jobId}/user`, {
					pageSize: "300",
				}),
			staleTime: 30_000,
		})
		.then((res) => res.users)
		.catch((error) => {
			// Jobs hidden for consultants cannot take requests, the backend answers 403
			if (error?.response?.status === 403)
				throw new Error("Det går inte att önska konsulter till det här uppdraget eftersom det är dolt för konsulter.");
			throw new Error("Det gick inte att hämta konsulter just nu. Försök igen om en stund.");
		});
}

const getErrorCode = (error: any) =>
	error?.response?.data?.detail || error?.response?.data?.code || error?.message || "UNKNOWN_ERROR";

/**
 * Mutations used by the week schedule. Every one of them refreshes the schedule.
 */
export function useScheduleActions(onSuccess: (message: string) => void, onError: (errorCode: string) => void) {
	const queryClient = useQueryClient();

	const refresh = (jobId: string) => {
		queryClient.invalidateQueries({ queryKey: ["useJobSchedule"] });
		queryClient.invalidateQueries({ queryKey: [SCHEDULE_QUERY_KEYS.jobDetail, jobId] });
		queryClient.invalidateQueries({ queryKey: [SCHEDULE_QUERY_KEYS.applications, jobId] });
		queryClient.invalidateQueries({ queryKey: ["job", jobId] });
		queryClient.invalidateQueries({ queryKey: ["jobs"] });
	};

	/**
	 * PUT /job/:id/shift replaces the whole shift list (shifts left out are
	 * removed), so the job's current shifts are always read fresh and `change`
	 * is applied on top of them.
	 */
	const updateShifts = useMutation({
		mutationFn: async ({ jobId, change }: { jobId: string; message: string; change: (shifts: ScheduleShiftInput[]) => ScheduleShiftInput[] }) => {
			const job = await apiGet<Job>(`/job/${jobId}`, { expand: "shifts" });
			const current: ScheduleShiftInput[] = (job.shifts || []).map(({ id, start, end, flexible, break: breakVal }: any) => ({
				id,
				start,
				end,
				flexible,
				break: breakVal,
			}));
			return apiPut(`/job/${jobId}/shift`, change(current));
		},
		onSuccess: (_data, { jobId, message }) => {
			refresh(jobId);
			onSuccess(message);
		},
		onError: (error) => onError(getErrorCode(error)),
	});

	/**
	 * One invitation per consultant (there is no batch endpoint). The answer to
	 * "book another consultant if the requested ones cannot?" is stored on the
	 * job as `allowLaterBooking`, only written when it changed.
	 */
	const inviteConsultants = useMutation({
		mutationFn: async ({
			jobId,
			consultantIds,
			allowLaterBooking,
		}: {
			jobId: string;
			consultantIds: string[];
			allowLaterBooking?: boolean;
		}) => {
			for (const consultantId of consultantIds) {
				await apiPost(`/job/${jobId}/invitation`, { consultant: { id: consultantId } });
			}
			if (allowLaterBooking !== undefined) await apiPut(`/job/${jobId}`, { allowLaterBooking });
		},
		onSuccess: (_data, { jobId, consultantIds }) => {
			refresh(jobId);
			queryClient.invalidateQueries({ queryKey: ["schedule-matching-consultants", jobId] });
			onSuccess(consultantIds.length === 1 ? "Inbjudan har skickats till konsulten" : "Inbjudningarna har skickats");
		},
		onError: (error, { jobId }) => {
			// Some invitations may have gone through before the failure
			refresh(jobId);
			queryClient.invalidateQueries({ queryKey: ["schedule-matching-consultants", jobId] });
			onError(getErrorCode(error));
		},
	});

	const bookApplication = useMutation({
		mutationFn: ({ jobId, applicationId }: { jobId: string; applicationId: string }) =>
			apiPut(`/job/${jobId}/application/${applicationId}`, { status: "ACCEPTED" }),
		onSuccess: (_data, { jobId }) => {
			refresh(jobId);
			onSuccess("Konsulten är bokad");
		},
		onError: (error) => onError(getErrorCode(error)),
	});

	return {
		/** Resolves with whether the update went through; errors are reported through `onError` */
		updateShifts: (vars: Parameters<typeof updateShifts.mutateAsync>[0]) =>
			updateShifts.mutateAsync(vars).then(
				() => true,
				() => false,
			),
		/** Rejects on failure so the drawer can stay open */
		inviteConsultants: (vars: Parameters<typeof inviteConsultants.mutateAsync>[0]) =>
			inviteConsultants.mutateAsync(vars).then(() => undefined),
		bookApplication: bookApplication.mutate,
		isSaving: updateShifts.isPending || inviteConsultants.isPending || bookApplication.isPending,
		isInviting: inviteConsultants.isPending,
	};
}
